import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR, STATUS_STYLE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ShoppingCart, ClipboardCheck, Truck, TrendingUp,
  AlertCircle, Clock, ArrowUpRight, BarChart3, Users,
} from "lucide-react";

const KPI = ({ label, value, icon: Icon, hint, accent = "blue" }) => {
  const colors = {
    blue:  { bg: "bg-blue-50",   text: "text-blue-700"   },
    green: { bg: "bg-emerald-50",text: "text-emerald-700" },
    amber: { bg: "bg-amber-50",  text: "text-amber-700"  },
    rose:  { bg: "bg-rose-50",   text: "text-rose-700"   },
  };
  const c = colors[accent] || colors.blue;
  return (
    <Card className="p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
          <div className="mt-2 text-2xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        <div className={`p-2 rounded-md ${c.bg} ${c.text}`}><Icon size={18} strokeWidth={1.5} /></div>
      </div>
    </Card>
  );
};

/**
 * Manager Dashboard
 * Accessible to: manager, accounts, admin
 *
 * Shows aggregate KPIs, regional breakdown, and (when SAP is live) outstanding balances.
 * Currently uses the existing /dashboard/stats API for order data.
 * The outstanding/SAP panel is a placeholder.
 */
export default function ManagerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
  }, []);

  if (!stats) return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
      <Skeleton className="h-80" />
    </div>
  );

  const totalValue = fmtINR(stats.revenue || 0);

  return (
    <div className="space-y-8" data-testid="manager-dashboard-page">

      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Management View</div>
        <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
          Manager Dashboard
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Aggregate pipeline view
          {user?.role === "manager" && user?.area ? ` · ${user.area} region` : " · All regions"}.
        </p>
      </div>

      {/* Order KPIs */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-3 flex items-center gap-2">
          <BarChart3 size={13} /> Order Pipeline
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Total POs"        value={stats.total_po}  icon={ShoppingCart}  hint={`${stats.submitted} pending`}     accent="blue"  />
          <KPI label="Approved"         value={stats.approved}  icon={ClipboardCheck}                                         accent="green" />
          <KPI label="SOs Raised"       value={stats.total_so}  icon={Truck}         hint={`${stats.fulfilled} fulfilled`}    accent="blue"  />
          <KPI label="Committed Value"  value={totalValue}      icon={TrendingUp}                                             accent="green" />
        </div>
      </div>

      {/* Outstanding — SAP Placeholder */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-3 flex items-center gap-2">
          <AlertCircle size={13} /> Outstanding Balances (SAP)
        </div>
        <Card className="p-6 border-amber-200 bg-amber-50">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-full bg-amber-100 text-amber-700 shrink-0">
              <Clock size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="font-medium text-amber-900">SAP Integration Pending</div>
              <div className="text-sm text-amber-800 mt-1">
                Real-time dealer outstanding data will appear here once SAP B1 is connected.
                Payments are recorded directly in SAP by the accounts team.
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["Total Outstanding", "0–30 Days", "30–60 Days", "60+ Days"].map((label) => (
                  <div key={label} className="p-3 bg-white rounded-md border border-amber-100">
                    <div className="text-[10px] text-amber-700 uppercase tracking-wide">{label}</div>
                    <div className="mt-1 text-lg font-medium text-slate-400">—</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent POs & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Recent Purchase Orders</div>
              <h2 className="text-xl font-medium tracking-tight mt-1" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Latest activity</h2>
            </div>
            <Link to="/po" className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="link-view-all-po">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {stats.recent.length === 0 && <div className="py-10 text-center text-sm text-slate-500">No POs yet.</div>}
            {stats.recent.map(po => {
              const s = STATUS_STYLE[po.status] || STATUS_STYLE.draft;
              return (
                <Link to={`/po/${po.po_id}`} key={po.po_id} className="flex items-center justify-between py-3 hover:bg-slate-50 rounded-md transition-colors px-2 -mx-2">
                  <div>
                    <div className="text-sm font-medium">{po.po_number}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{po.dealer_name} · {po.dealer_area}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm font-medium">{fmtINR(po.total)}</div>
                    <Badge variant="outline" className={`${s.bg} ${s.text} ${s.border}`}>{s.label}</Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>

        <div className="space-y-4">
          {/* Top Products */}
          <Card className="p-6">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Top Moving SKUs</div>
            <h2 className="text-xl font-medium tracking-tight mt-1" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Product velocity</h2>
            <div className="mt-4 space-y-3">
              {stats.top_products.length === 0 && <div className="text-sm text-slate-500 py-4">No data yet.</div>}
              {stats.top_products.map(p => (
                <div key={p.sku} className="flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{p.sku}</div>
                  </div>
                  <div className="text-slate-700 font-medium">{p.qty} u</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Quick Links */}
          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">Quick Access</div>
            <div className="space-y-2">
              <Link to="/outstanding" className="flex items-center justify-between text-sm hover:bg-slate-50 rounded-md px-2 py-2 -mx-2 transition-colors text-slate-700">
                <span className="flex items-center gap-2"><AlertCircle size={14} className="text-amber-500" /> Outstanding</span>
                <ArrowUpRight size={14} className="text-slate-400" />
              </Link>
              <Link to="/inventory" className="flex items-center justify-between text-sm hover:bg-slate-50 rounded-md px-2 py-2 -mx-2 transition-colors text-slate-700">
                <span className="flex items-center gap-2"><Users size={14} className="text-blue-500" /> Inventory</span>
                <ArrowUpRight size={14} className="text-slate-400" />
              </Link>
              <Link to="/so" className="flex items-center justify-between text-sm hover:bg-slate-50 rounded-md px-2 py-2 -mx-2 transition-colors text-slate-700">
                <span className="flex items-center gap-2"><Truck size={14} className="text-purple-500" /> Sales Orders</span>
                <ArrowUpRight size={14} className="text-slate-400" />
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
